import React, { useCallback, useImperativeHandle, useRef, useState } from "react";
import { isEqual, isFunction } from "@util/utils";
import CycleEffectOrderDeleteDialog from "./CycleEffectOrderDeleteDialog";
import CyclePurchaseDeleteDialog from "./CyclePurchaseDeleteDialog";
import CyclePurchaseInvoiceDialog from "./CyclePurchaseInvoiceDialog";
import CyclePurchasePaymentDialog from "./CyclePurchasePaymentDialog";
import CyclePurchaseShippingDialog from "./CyclePurchaseShippingDialog";
import CyclePurchaseEffectDialog from "./CyclePurchaseEffectDialog";
import CyclePurchaseTipDialog from "./CyclePurchaseTipDialog";
import HealthyCycleFirstOrderDialog from "./HealthyCycleFirstOrderDialog";
import CyclePurchaseCoachChangeDialog from "./CyclePurchaseCoachChangeDialog";

const CYCLE_EFFECT_ORDER_DELETE_DIALOG = "CycleEffectOrderDeleteDialog";
const CYCLE_PURCHASE_DELETE_DIALOG = "CyclePurchaseDeleteDialog";
const CYCLE_PURCHASE_INVOICE_DIALOG = "CyclePurchaseInvoiceDialog";
const CYCLE_PURCHASE_PAYMENT_DIALOG = "CyclePurchasePaymentDialog";
const CYCLE_PURCHASE_SHIPPING_DIALOG = "CyclePurchaseShippingDialog";
const CYCLE_PURCHASE_EFFECT_DIALOG = "CyclePurchaseEffectDialog";
const CYCLE_PURCHASE_TIP_DIALOG = "CyclePurchaseTipDialog";
const HEALTHY_CYCLE_FIRST_ORDER_DIALOG = "HealthyCycleFirstOrderDialog";
const CYCLE_PURCHASE_COACH_CHANGE_DIALOG = "CyclePurchaseCoachChangeDialog";

const CyclePurchaseDialogBox = React.forwardRef((props, ref) => {
	const [openDialog, setOpenDialog] = useState("");
	const [sourceData, setSourceData] = useState(null);
	const refreshRef = useRef(null);

	const handleDialogClose = useCallback(
		() => {
			setOpenDialog(null);
			setSourceData(null);
		},
		// eslint-disable-next-line
		[],
	);

	const doRefresh = (...arg) => {
		if (isFunction(refreshRef.current)) {
			setTimeout(() => refreshRef.current(...arg), 5);
		}
	};

	useImperativeHandle(
		ref,
		() => ({
			// 開啟刪除健康產品變更單彈窗
			openCycleEffectOrderDeleteDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(CYCLE_EFFECT_ORDER_DELETE_DIALOG);
			},
			// 開啟刪除健康產品循環訂購單彈窗
			openCyclePurchaseDeleteDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(CYCLE_PURCHASE_DELETE_DIALOG);
			},
			// 開啟發票資訊彈窗
			openCyclePurchaseInvoiceDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(CYCLE_PURCHASE_INVOICE_DIALOG);
			},
			// 開啟付款資訊彈窗
			openCyclePurchasePaymentDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(CYCLE_PURCHASE_PAYMENT_DIALOG);
			},
			// 開啟收件人資訊彈窗
			openCyclePurchaseShippingDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(CYCLE_PURCHASE_SHIPPING_DIALOG);
			},
			// 開啟健康產品(唯讀)變更單彈窗
			openCyclePurchaseEffectDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(CYCLE_PURCHASE_EFFECT_DIALOG);
			},
			// 開啟健康產品提醒彈窗
			openCyclePurchaseTipDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(CYCLE_PURCHASE_TIP_DIALOG);
			},
			// 開啟循環訂單首購彈窗
			openHealthyCycleFirstOrderDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(HEALTHY_CYCLE_FIRST_ORDER_DIALOG);
			},
			// 開啟變更建單擔當彈窗
			openCyclePurchaseCoachChangeDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(CYCLE_PURCHASE_COACH_CHANGE_DIALOG);
			},
			// 關閉彈窗
			closeDialog: () => {
				handleDialogClose();
			},
		}),
		// eslint-disable-next-line
		[],
	);

	return (
		<React.Fragment>
			{/* 刪除健康產品變更單 */}
			<CycleEffectOrderDeleteDialog open={isEqual(openDialog, CYCLE_EFFECT_ORDER_DELETE_DIALOG)} onClose={handleDialogClose} refresh={doRefresh} sourceData={sourceData} />
			{/* 刪除健康產品循環訂購單 */}
			<CyclePurchaseDeleteDialog open={isEqual(openDialog, CYCLE_PURCHASE_DELETE_DIALOG)} onClose={handleDialogClose} refresh={doRefresh} sourceData={sourceData} />
			{/* 循環訂購單發票資訊 */}
			<CyclePurchaseInvoiceDialog open={isEqual(openDialog, CYCLE_PURCHASE_INVOICE_DIALOG)} onClose={handleDialogClose} sourceData={sourceData} />
			{/* 循環訂購單付款資訊 */}
			<CyclePurchasePaymentDialog open={isEqual(openDialog, CYCLE_PURCHASE_PAYMENT_DIALOG)} onClose={handleDialogClose} sourceData={sourceData} />
			{/* 循環訂購單收件人資訊 */}
			<CyclePurchaseShippingDialog open={isEqual(openDialog, CYCLE_PURCHASE_SHIPPING_DIALOG)} onClose={handleDialogClose} sourceData={sourceData} />
			{/* 健康產品(唯讀)變更單 */}
			<CyclePurchaseEffectDialog open={isEqual(openDialog, CYCLE_PURCHASE_EFFECT_DIALOG)} onClose={handleDialogClose} sourceData={sourceData} />
			{/* 健康產品提醒彈窗 */}
			<CyclePurchaseTipDialog open={isEqual(openDialog, CYCLE_PURCHASE_TIP_DIALOG)} onClose={handleDialogClose} sourceData={sourceData} />
			{/* 循環訂單首購彈窗 */}
			<HealthyCycleFirstOrderDialog open={isEqual(openDialog, HEALTHY_CYCLE_FIRST_ORDER_DIALOG)} onClose={handleDialogClose} refresh={doRefresh} sourceData={sourceData} />
			{/* 變更建單擔當 */}
			<CyclePurchaseCoachChangeDialog open={isEqual(openDialog, CYCLE_PURCHASE_COACH_CHANGE_DIALOG)} onClose={handleDialogClose} refresh={doRefresh} sourceData={sourceData} />
		</React.Fragment>
	);
});

export default CyclePurchaseDialogBox;
